import { AppButton, Chip } from '@clickguard/design-system'
import { useBreadcrumbSelection } from 'components/breadcrumb-bar'
import { PageLayout } from 'components/page-layout'
import {
	ConfigurationPanel,
	EmptyNoAdAccount,
	EmptyPostSetup,
	SetupAccordion,
	StandardView,
	useRefundsAgentState,
	ViewSubmissionDrawer
} from 'components/refunds-agent'
import Settings01 from '@untitled-ui/icons-react/build/esm/Settings01'
import { useCallback, useEffect, useState } from 'react'
import { Navigate, useParams, useSearchParams } from 'react-router-dom'
import { Notices } from 'components/notice/notices'
import { useFeatureFlag } from 'services/feature-flags'
import { useAccountStore, useAssetStore } from 'store'
import { PageTitle } from 'utils/page-title'
import { useTrackPostHogEvent } from 'utils/posthog'
import { PostHogEvent, PostHogPath } from 'utils/posthog-events'
import { useShallow } from 'zustand/react/shallow'
import { fetchRefunds } from './api'
import { RefundRequestRow, RefundsRange, RefundsResponse } from './types'

export const RefundsPage = () => {
	const { accountId, assetId } = useParams<{
		accountId: string
		assetId: string
	}>()
	const refundsEnabled = useFeatureFlag('refunds-agent-enabled')
	const trackPostHogEvent = useTrackPostHogEvent()

	const { adAccounts, asset, demoMode } = useAssetStore(
		useShallow((s) => ({
			adAccounts: s.adAccounts,
			asset: s.asset,
			demoMode: s.demoMode
		}))
	)
	const { account, isInSetup, hasFeature } = useAccountStore(
		useShallow((s) => ({
			account: s.account,
			isInSetup: s.isInSetup,
			hasFeature: s.hasFeature
		}))
	)
	const { firstAdAccountId } = useBreadcrumbSelection()
	const [, setSearchParams] = useSearchParams()

	const [data, setData] = useState<RefundsResponse | null>(null)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState<string | null>(null)
	const [range, setRange] = useState<RefundsRange>('1m')
	const [gearOpen, setGearOpen] = useState(false)
	const [detailsItem, setDetailsItem] = useState<RefundRequestRow | null>(null)

	// PostHog pageview (§Monitoring).
	useEffect(() => {
		if (!refundsEnabled) return
		trackPostHogEvent(
			PostHogEvent.PAGEVIEW_REFUNDS_AGENT,
			PostHogPath.REFUNDS_AGENT,
			null
		)
	}, [refundsEnabled])

	// Pre-select the most-recently connected active Google Ads account (§2) whenever the
	// current selection isn't a valid GAds account — i.e. nothing selected, or a non-GAds
	// id carried in from another page via the shared `adAccounts` param (that id can't be
	// shown by this page's gads-only selector, so without this the page would be stranded).
	useEffect(() => {
		if (adAccounts.length === 0) return
		const gads = adAccounts.filter(
			(a) => a.platform === 'GADS' && a.status === 'ACTIVE'
		)
		if (gads.length === 0) return
		const selectionIsGads =
			!!firstAdAccountId && gads.some((a) => a.id === firstAdAccountId)
		if (selectionIsGads) return
		const mostRecent = gads
			.slice()
			.sort((a, b) => {
				const ta = new Date(a.activated_at ?? a.created_at ?? 0).getTime()
				const tb = new Date(b.activated_at ?? b.created_at ?? 0).getTime()
				return tb - ta
			})[0]
		const next = new URLSearchParams(window.location.search)
		next.set('adAccounts', mostRecent.id)
		setSearchParams(next, { replace: true })
	}, [firstAdAccountId, adAccounts, setSearchParams])

	const loadRefunds = useCallback(() => {
		if (!accountId || !refundsEnabled) return
		setLoading(true)
		setError(null)
		fetchRefunds(accountId, {
			adAccountId: firstAdAccountId ?? undefined,
			range
		})
			.then((d) => {
				setData(d)
				setLoading(false)
			})
			.catch((e: Error) => {
				setError(e.message)
				setLoading(false)
			})
	}, [accountId, refundsEnabled, firstAdAccountId, range])

	useEffect(() => {
		loadRefunds()
	}, [loadRefunds])

	const state = useRefundsAgentState(
		adAccounts,
		firstAdAccountId,
		data?.items ?? []
	)

	if (!refundsEnabled) {
		return <Navigate to=".." replace />
	}

	const showConfigAction =
		state.view === 'empty' || state.view === 'standard'

	return (
		<>
			<PageTitle pageName="Refunds Agent" />
			<PageLayout
				title="Refunds Agent"
				subtitle="AI agent to submit Google Ads refund requests on autopilot."
				titleBadge={
					<Chip color="gray" size="md">
						Beta
					</Chip>
				}
				notices={
					<Notices
						isInSetup={isInSetup()}
						setupStep={account?.setup?.step}
						isInActive={account?.status === 'INACTIVE'}
						hasDashboardPerformanceFeature={hasFeature('dashboard_performance')}
						tc_installed={!!asset?.tc_installed}
						cc_installed={!!asset?.cc_installed}
						tc_installed_skipped={!!asset?.tc_installed_skipped}
						accountId={accountId}
						assetId={assetId}
						demoMode={demoMode}
						mccLinkInviteAdAccounts={adAccounts}
					/>
				}
				topAction={
					showConfigAction ? (
						<AppButton
							variant="secondary"
							roundness="default"
							size="md"
							leftIcon={<Settings01 className="h-4 w-4" />}
							label="Configuration"
							onClick={() => setGearOpen(true)}
						/>
					) : undefined
				}
			>
				{loading ? (
					<div className="space-y-6">
						<div className="grid grid-cols-1 lg:grid-cols-[minmax(0,492px)_minmax(0,1fr)] gap-6">
							<div className="h-[320px] rounded-2xl bg-table-band-subtle animate-pulse" />
							<div className="h-[320px] rounded-2xl bg-table-band-subtle animate-pulse" />
						</div>
						<div className="h-[280px] rounded-2xl bg-table-band-subtle animate-pulse" />
					</div>
				) : error ? (
					<div className="p-8 text-red-600 bg-red-50 border border-red-200 rounded-3xl">
						Failed to load refunds: {error}
					</div>
				) : state.view === 'no-account' ? (
					<EmptyNoAdAccount />
				) : state.view === 'setup' ? (
					state.selected && accountId ? (
						<SetupAccordion
							accountId={accountId}
							adAccount={state.selected}
							onLaunched={loadRefunds}
						/>
					) : (
						<div className="h-[320px] rounded-2xl bg-table-band-subtle animate-pulse" />
					)
				) : state.view === 'empty' ? (
					<EmptyPostSetup
						nextSubmissionAt={data?.summary.next_submission_at ?? null}
						onRefresh={loadRefunds}
						refreshing={loading}
					/>
				) : data ? (
					<StandardView
						data={data}
						accountName={state.selected?.name ?? ''}
						range={range}
						onRangeChange={setRange}
						onRefresh={loadRefunds}
						refreshing={loading}
						onView={setDetailsItem}
					/>
				) : null}

				{state.selected && accountId && (
					<ConfigurationPanel
						open={gearOpen}
						onClose={() => setGearOpen(false)}
						accountId={accountId}
						adAccount={state.selected}
						items={data?.items ?? []}
					/>
				)}

				<ViewSubmissionDrawer
					open={detailsItem !== null}
					onClose={() => setDetailsItem(null)}
					item={detailsItem}
				/>
			</PageLayout>
		</>
	)
}
